/**
 * FP3-029B3 + FP3-029B4 — Provider Detail / Control page.
 *
 *   /admin/providers/:providerId
 *
 * Admin-family hub for one provider:
 *   - Snapshot (name, email, role, scope, status, caseload, today,
 *     health, phone, birthday, team-facing personal status)
 *   - Inline edit for phone + birthday (admin family only)
 *   - School chips with add/remove (reuses FP3-029B2 attach/detach
 *     API)
 *   - Provider status pill (super_admin / org_admin only)
 *   - Quick actions: read-only workspace · message · shared calendar
 *   - Collapsible Active Students section (FP3-029B4)
 *
 * The admin↔provider DM affordance remains a disabled "Soon" chip;
 * provider-to-provider private messages must never surface to admins.
 *
 * AdminShell is rendered in `compact` mode — the top admin subnav and
 * the "Admin View · Coordination & Oversight" banner are hidden
 * because the left sidebar already conveys both contexts.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CaretLeft,
  Users,
  UsersThree,
  Buildings,
  GraduationCap,
  Eye,
  Envelope,
  Plus,
  X,
  CalendarBlank,
  ChartLineUp,
  Heartbeat,
  Target,
  CircleHalf,
  Phone,
  Cake,
  PencilSimple,
  ChatCircleDots,
  CaretDown,
  CaretUp,
  PuzzlePiece,
  Prohibit,
  ArrowCounterClockwise,
  Trash,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminShell from "./AdminShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import CoveragePanel from "@/components/admin/CoveragePanel";
import DisableProviderModal from "@/components/admin/DisableProviderModal";
import PlatformProviderDeleteModal from "@/components/platform/PlatformProviderDeleteModal";

const PROVIDER_STATUS_VALUES = [
  { value: "available", label: "Available", tone: "ok" },
  { value: "on_leave", label: "On leave", tone: "info" },
  { value: "paused", label: "Paused", tone: "warn" },
  { value: "unavailable", label: "Unavailable", tone: "warn" },
  { value: "inactive", label: "Inactive", tone: "danger" },
];

const TONES = {
  ok: { bg: "#E8F0EB", fg: "#1E3F33" },
  info: { bg: "#E7EDF3", fg: "#1F3F4F" },
  warn: { bg: "#F5EFE3", fg: "#7A6A2C" },
  danger: { bg: "#F8EFEA", fg: "#7A3A2C" },
  default: { bg: "#EEF1F6", fg: "#3F4756" },
};

export default function AdminProviderDetail() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [allSchools, setAllSchools] = useState([]);
  // FP3-029C — Org-scoped provider list for the Coverage / Reassignment
  // pickers. Loaded once per page mount.
  const [allProvidersForCoverage, setAllProvidersForCoverage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedSchool, setPickedSchool] = useState("");
  const [statusOpen, setStatusOpen] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState({ phone: "", birthday: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [studentsOpen, setStudentsOpen] = useState(false);
  // FP3-105A — account disable/reactivate + permanent delete.
  const [accountAction, setAccountAction] = useState(null); // "disable"|"reactivate"
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isSuperAdmin = (user?.permission_role || "") === "super_admin";
  const canEditStatus = ["admin", "org_admin", "super_admin"].includes(
    user?.permission_role || ""
  );
  const canEditProfile = canEditStatus; // same admin family gate

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getAdminProviderDetail(providerId);
      setProvider(r.data);
      setProfileDraft({
        phone: r.data.phone || "",
        birthday: r.data.birthday || "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not load provider"
      );
      navigate("/admin/providers", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [providerId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .listAdminSchools({})
      .then((r) => setAllSchools(r.data || []))
      .catch(() => setAllSchools([]));
    // FP3-029C — Hydrate the org-scoped provider list once per mount.
    api
      .getAdminProviders()
      .then((r) => setAllProvidersForCoverage(r.data || []))
      .catch(() => setAllProvidersForCoverage([]));
  }, []);

  const attachSchool = async () => {
    if (!pickedSchool || !provider?.user_id) return;
    try {
      await api.attachUserSchool(provider.user_id, pickedSchool);
      toast.success("School added");
      setPickerOpen(false);
      setPickedSchool("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add school");
    }
  };

  const detachSchool = async (schoolId, schoolName) => {
    if (!provider?.user_id) return;
    try {
      await api.detachUserSchool(provider.user_id, schoolId);
      toast.success(`Removed ${schoolName}`);
      load();
    } catch (err) {
      toast.warning(
        err?.response?.data?.detail || "Could not remove school"
      );
    }
  };

  const setProviderStatus = async (newStatus) => {
    if (!provider?.user_id) return;
    setSavingStatus(true);
    try {
      await api.setProviderStatus(provider.user_id, newStatus);
      toast.success("Provider status updated");
      setStatusOpen(false);
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not update status"
      );
    } finally {
      setSavingStatus(false);
    }
  };

  const saveProfile = async () => {
    if (!provider?.user_id) return;
    setSavingProfile(true);
    try {
      await api.updateProviderProfile(provider.user_id, {
        phone: profileDraft.phone || "",
        birthday: profileDraft.birthday || "",
      });
      toast.success("Profile updated");
      setEditingProfile(false);
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not update profile"
      );
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading || !provider) {
    return (
      <AdminShell title="Provider" subtitle="Loading…" compact>
        <Card className="border-[#E5E3DB] rounded-2xl">
          <CardContent className="p-6 text-[#6B7280] text-center text-[12.5px]">
            Loading provider…
          </CardContent>
        </Card>
      </AdminShell>
    );
  }

  const fullName = provider.name || `${provider.first_name} ${provider.last_name}`;
  const initials = `${(provider.first_name || "")[0] || ""}${(provider.last_name || "")[0] || ""}`.toUpperCase();
  const assigned = new Set(provider.school_ids || []);
  const orgScopedSchools = allSchools.filter(
    (s) => !provider.organization_id || s.organization_id === provider.organization_id
  );
  const available = orgScopedSchools.filter((s) => !assigned.has(s.id));
  const statusTone = (PROVIDER_STATUS_VALUES.find(
    (v) => v.value === provider.provider_status
  ) || { tone: "default" }).tone;

  // FP3-029B6A — Route-target list for the per-student picker on the
  // Active Students card. Only available when this provider is a
  // primary AND the caller is admin family (in this admin surface
  // we always are). The supervising primary remains as one option
  // (route back to self), and every active assistant under them is
  // listed by name. Inactive assistants are excluded.
  const isAdminFamilyCaller = ["admin", "org_admin", "super_admin"].includes(
    user?.permission_role || ""
  );
  const canRoute =
    isAdminFamilyCaller &&
    provider.provider_role_type === "primary" &&
    (provider.assistants || []).some((a) => a.supervision_status === "active");
  const routeTargets = canRoute
    ? [
        {
          id: provider.id,
          // FP3-076A-2 — Was "(primary)" — renamed so the routing
          // picker reads as "this is the credentialed provider"
          // not "this is the student-level Primary Provider".
          label: `${provider.name} (provider)`,
          assistant_type: "",
        },
        ...(provider.assistants || [])
          .filter((a) => a.supervision_status === "active")
          .map((a) => ({
            id: a.provider_id,
            label: a.name,
            assistant_type: a.assistant_type,
          })),
      ]
    : [];

  return (
    <AdminShell title={fullName} subtitle={provider.role || "Provider"} compact>
      {/* Back link */}
      <div className="mb-3">
        <Link
          to="/admin/providers"
          data-testid="provider-detail-back"
          className="inline-flex items-center gap-1 text-[12px] text-[#6B7280] hover:text-[#1F2937]"
        >
          <CaretLeft size={12} weight="bold" /> Back to Providers
        </Link>
      </div>

      {/* Snapshot card */}
      <Card
        data-testid="provider-detail-snapshot"
        className="border-[#E5E3DB] rounded-2xl mb-4"
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <Avatar className="w-14 h-14 bg-[#E8F0EB] text-[#1E3F33] text-[18px] font-semibold">
              <AvatarFallback className="bg-[#E8F0EB] text-[#1E3F33]">
                {initials || "P"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h2 className="text-[18px] font-semibold text-[#1F2937] heading-font tracking-tight">
                {fullName}
              </h2>
              <p className="text-[12px] text-[#6B7280] mt-0.5">
                {provider.role || "—"}
                {provider.email && (
                  <span className="ml-2 text-[#9AA0A6]">· {provider.email}</span>
                )}
              </p>
              {/* FP3-029B4 — Team-facing personal status, set by the
                  provider themselves. Shown as a calm chip; empty
                  state is not rendered to avoid noise. */}
              {provider.personal_status && (
                <p
                  data-testid="provider-detail-personal-status"
                  className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] text-[#3F4756] bg-[#F5EFE3] px-2 py-0.5 rounded-full max-w-full"
                  title="Team-facing status set by the provider"
                >
                  <ChatCircleDots size={11} weight="duotone" className="shrink-0" />
                  <span className="truncate">{provider.personal_status}</span>
                </p>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                <ScopeChip scope={provider.access_scope} />
                <StatusChip
                  status={provider.provider_status}
                  tone={statusTone}
                  testId="provider-detail-status-chip"
                />
                {/* FP3-105A — ACCOUNT status badge (distinct from the
                    availability chip above). Disabled = login blocked. */}
                {["deactivated", "disabled", "removed"].includes(
                  (provider.account_status || "").toLowerCase()
                ) ? (
                  <span
                    data-testid="provider-detail-account-disabled-badge"
                    className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold px-2 py-0.5 rounded-full bg-[#F8EFEA] text-[#8A2E1E] border border-[#E3C4BE]"
                  >
                    <Prohibit size={9} weight="bold" /> Disabled
                  </span>
                ) : (
                  <span
                    data-testid="provider-detail-account-active-badge"
                    className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full bg-[#E8F0EB] text-[#1E5A3F]"
                  >
                    Active
                  </span>
                )}
                {/* FP3-029B6 — Provider type pill: Primary vs Assistant.
                    For assistants, also surface the discipline subtype
                    and a deep-link to the supervising provider. */}
                {provider.provider_role_type === "assistant" ? (
                  <>
                    <Chip
                      tone="info"
                      label={`Assistant${provider.assistant_type ? ` · ${provider.assistant_type}` : ""}`}
                    />
                    {(() => {
                      // FP3-090 — one deep-link chip per authorized
                      // supervisor (order carries no priority).
                      const sups = (provider.supervising_providers || []).length
                        ? provider.supervising_providers
                        : provider.supervising_provider
                          ? [provider.supervising_provider]
                          : [];
                      return sups.map((sp, idx) => (
                        <Link
                          key={sp.id || idx}
                          to={`/admin/providers/${sp.id}`}
                          data-testid={idx === 0
                            ? "provider-detail-supervising-link"
                            : `provider-detail-supervising-link-${idx}`}
                          className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full bg-[#EEF1F6] text-[#3F4756] hover:bg-[#E0E5EE]"
                          title="Open supervising provider"
                        >
                          <UsersThree size={9} weight="duotone" />
                          Supervised by {sp.name}
                        </Link>
                      ));
                    })()}
                  </>
                ) : (
                  // FP3-076A-2 — Was "Primary provider" — renamed so this
                  // page-level badge never implies a global per-student
                  // ownership identity. "Provider" matches the table
                  // chip on /admin/providers; per-student Primary
                  // Provider designation lives on the student team.
                  <Chip tone="default" label="Provider" />
                )}
              </div>
              {canEditStatus && (
                <ProviderStatusPicker
                  open={statusOpen}
                  current={provider.provider_status}
                  saving={savingStatus}
                  onToggle={() => setStatusOpen((o) => !o)}
                  onPick={setProviderStatus}
                />
              )}
            </div>
            <div className="text-right space-y-1.5">
              <Link
                to={`/?as_provider=${provider.id}`}
                data-testid="provider-detail-read-only"
                className="inline-flex items-center gap-1.5 text-[12px] text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
              >
                <Eye size={12} weight="duotone" /> Open read-only workspace
              </Link>
            </div>
          </div>

          {/* FP3-029B4 — Profile fields row (phone + birthday) with
              inline edit. */}
          <div
            data-testid="provider-detail-profile-fields"
            className="mt-4 pt-4 border-t border-[#F1F2F4]"
          >
            {!editingProfile ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-4 flex-wrap">
                  <ProfileField
                    icon={Phone}
                    label="Phone"
                    value={provider.phone}
                    placeholder="—"
                    testId="provider-detail-phone"
                  />
                  <ProfileField
                    icon={Cake}
                    label="Birthday"
                    value={formatBirthday(provider.birthday)}
                    placeholder="—"
                    testId="provider-detail-birthday"
                  />
                </div>
                {canEditProfile && (
                  <Button
                    data-testid="provider-detail-edit-profile"
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingProfile(true)}
                    className="h-7 text-[11.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
                  >
                    <PencilSimple size={11} className="mr-1" /> Edit
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-[11.5px] text-[#3F4756]">
                  <span className="flex items-center gap-1 mb-1">
                    <Phone size={11} weight="duotone" /> Phone
                  </span>
                  <input
                    data-testid="provider-detail-phone-input"
                    type="tel"
                    value={profileDraft.phone}
                    onChange={(e) =>
                      setProfileDraft((d) => ({ ...d, phone: e.target.value }))
                    }
                    placeholder="555-0142"
                    className="w-full h-8 px-2 text-[12px] border border-[#E5E3DB] rounded-md bg-white focus:outline-none focus:border-[#1E3F33]"
                  />
                </label>
                <label className="text-[11.5px] text-[#3F4756]">
                  <span className="flex items-center gap-1 mb-1">
                    <Cake size={11} weight="duotone" /> Birthday
                  </span>
                  <input
                    data-testid="provider-detail-birthday-input"
                    type="date"
                    value={profileDraft.birthday}
                    onChange={(e) =>
                      setProfileDraft((d) => ({ ...d, birthday: e.target.value }))
                    }
                    className="w-full h-8 px-2 text-[12px] border border-[#E5E3DB] rounded-md bg-white focus:outline-none focus:border-[#1E3F33]"
                  />
                </label>
                <div className="sm:col-span-2 flex items-center gap-2">
                  <Button
                    data-testid="provider-detail-save-profile"
                    size="sm"
                    onClick={saveProfile}
                    disabled={savingProfile}
                    className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingProfile(false);
                      setProfileDraft({
                        phone: provider.phone || "",
                        birthday: provider.birthday || "",
                      });
                    }}
                    className="h-8 text-[12px] border-[#E5E3DB] text-[#6B7280]"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-5 pt-4 border-t border-[#F1F2F4]">
            <Stat
              label="Caseload"
              value={provider.caseload_count ?? 0}
              icon={GraduationCap}
              testId="stat-caseload"
            />
            {/* FP3-029B6 — Supervised caseload tile only renders for
                primary providers with at least one assistant relationship,
                so we don't show a "0" tile to providers who don't
                supervise anyone. */}
            {provider.provider_role_type === "primary" && (provider.assistants || []).length > 0 && (
              <Stat
                label="Assistant-served"
                value={provider.supervised_caseload_count ?? 0}
                icon={UsersThree}
                testId="stat-supervised-caseload"
                tone="info"
              />
            )}
            <Stat
              label="Active goals"
              value={provider.active_goals_count ?? 0}
              icon={Target}
              testId="stat-goals"
            />
            <Stat
              label="Today (planned)"
              value={provider.today_sessions?.planned ?? 0}
              icon={CalendarBlank}
              testId="stat-today-planned"
            />
            <Stat
              label="Schedule health"
              value={
                provider.health_violations
                  ? `${provider.health_violations} issues`
                  : "Clear"
              }
              icon={Heartbeat}
              testId="stat-health"
              tone={provider.health_violations ? "warn" : "ok"}
            />
          </div>
        </CardContent>
      </Card>

      {/* FP3-029B6 — Assistants Supervised card (only for primary
          providers with an active or historical assistant link). */}
      {provider.provider_role_type === "primary" && (provider.assistants || []).length > 0 && (
        <Card
          data-testid="provider-detail-assistants-card"
          className="border-[#E5E3DB] rounded-2xl mb-4"
        >
          <CardContent className="p-5">
            <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5 mb-3">
              <UsersThree size={14} weight="duotone" className="text-[#3F4756]" />
              Assistants supervised
              <span className="ml-1.5 text-[11px] font-medium text-[#6B7280] bg-[#F3F1EC] rounded-full px-2 py-0.5 tabular-nums">
                {provider.assistants.filter((a) => a.supervision_status === "active").length}
              </span>
            </h3>
            <ul className="divide-y divide-[#F1F2F4]">
              {provider.assistants.map((a) => {
                const isActive = a.supervision_status === "active";
                return (
                  <li
                    key={a.user_id}
                    data-testid={`provider-detail-assistant-${a.provider_id}`}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/admin/providers/${a.provider_id}`}
                        className="text-[12.5px] font-medium text-[#1F2937] hover:text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
                      >
                        {a.name}
                      </Link>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {a.assistant_type && (
                          <span
                            className="inline-flex items-center text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: "#EEF1F6", color: "#3F4756" }}
                          >
                            {a.assistant_type}
                          </span>
                        )}
                        {!isActive && (
                          <span
                            className="inline-flex items-center text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: "#F1F2F4", color: "#6B7280" }}
                          >
                            Inactive
                          </span>
                        )}
                        {a.email && (
                          <span className="text-[10.5px] text-[#9AA0A6] ml-1 truncate">
                            {a.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-[10.5px] text-[#6B7280] shrink-0 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1">
                        <GraduationCap size={11} weight="duotone" />
                        {a.caseload_count}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="text-[10.5px] text-[#9AA0A6] mt-3 leading-snug">
              Assistant work remains attributed to the assistant. Supervising
              providers retain oversight visibility within their organization /
              school scope.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Active students card — FP3-029B4 */}
      <Card
        data-testid="provider-detail-active-students-card"
        className="border-[#E5E3DB] rounded-2xl mb-4"
      >
        <CardContent className="p-5">
          <button
            data-testid="provider-detail-active-students-toggle"
            onClick={() => setStudentsOpen((o) => !o)}
            className="w-full flex items-center justify-between gap-2 text-left"
          >
            <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
              <Users size={14} weight="duotone" className="text-[#3F4756]" />
              Active students
              <span className="ml-1.5 text-[11px] font-medium text-[#6B7280] bg-[#F3F1EC] rounded-full px-2 py-0.5 tabular-nums">
                {(provider.active_students || []).length}
              </span>
            </h3>
            {studentsOpen ? (
              <CaretUp size={14} className="text-[#6B7280]" />
            ) : (
              <CaretDown size={14} className="text-[#6B7280]" />
            )}
          </button>
          {studentsOpen && (
            <div
              data-testid="provider-detail-active-students-list"
              className="mt-3 pt-3 border-t border-[#F1F2F4]"
            >
              {(provider.active_students || []).length === 0 ? (
                <p className="text-[12px] text-[#6B7280] py-2">
                  No active students yet.
                </p>
              ) : (
                <ul className="divide-y divide-[#F1F2F4]">
                  {(provider.active_students || []).map((s) => (
                    <StudentRouteRow
                      key={s.id}
                      student={s}
                      provider={provider}
                      canRoute={canRoute}
                      routeTargets={routeTargets}
                      onAfterRoute={load}
                    />
                  ))}
                </ul>
              )}
              {canRoute && routeTargets.length > 1 && (
                <p className="text-[10.5px] text-[#9AA0A6] mt-3 leading-snug">
                  Route students between yourself and your active assistants.
                  Routing keeps historical attribution intact — past sessions
                  remain credited to the provider who logged them.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coverage & Reassignment panel — FP3-029C */}
      <CoveragePanel
        provider={provider}
        allProviders={allProvidersForCoverage}
        onChanged={load}
      />

      {/* Schools card */}
      <Card className="border-[#E5E3DB] rounded-2xl mb-4">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
              <Buildings size={14} weight="duotone" className="text-[#3F4756]" />
              Schools / Campuses
            </h3>
            {available.length > 0 && !pickerOpen && (
              <Button
                data-testid="provider-detail-add-school"
                size="sm"
                variant="outline"
                onClick={() => setPickerOpen(true)}
                className="h-7 text-[11.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
              >
                <Plus size={11} className="mr-1" /> Add school
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(provider.school_chips || []).map((s) => (
              <span
                key={s.id}
                data-testid={`provider-detail-school-${s.id}`}
                className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full bg-[#E8F0EB] text-[#1E3F33]"
              >
                <Buildings size={11} weight="duotone" />
                {s.name}
                <button
                  data-testid={`provider-detail-school-${s.id}-remove`}
                  onClick={() => detachSchool(s.id, s.name)}
                  className="ml-1 text-[#1E3F33]/60 hover:text-[#7A3A2C]"
                  title={`Remove ${s.name}`}
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            {(provider.school_chips || []).length === 0 && provider.access_scope === "assigned_schools" && (
              <span
                data-testid="provider-detail-no-schools"
                className="inline-flex items-center text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full"
                style={{ backgroundColor: "#F5EFE3", color: "#7A6A2C" }}
              >
                No schools assigned
              </span>
            )}
          </div>
          {pickerOpen && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#F1F2F4]">
              <select
                data-testid="provider-detail-school-select"
                value={pickedSchool}
                onChange={(e) => setPickedSchool(e.target.value)}
                className="h-8 border border-[#E5E3DB] rounded-md px-2 text-[12px] bg-white min-w-[200px]"
              >
                <option value="">— School —</option>
                {available.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <Button
                data-testid="provider-detail-school-submit"
                size="sm"
                onClick={attachSchool}
                disabled={!pickedSchool}
                className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
              >
                Add
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setPickerOpen(false);
                  setPickedSchool("");
                }}
                className="h-8 text-[12px] border-[#E5E3DB] text-[#6B7280]"
              >
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick actions card */}
      <Card className="border-[#E5E3DB] rounded-2xl">
        <CardContent className="p-5 space-y-2">
          <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font mb-2">
            Quick actions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <ActionLink
              testId="action-read-only-workspace"
              to={`/?as_provider=${provider.id}`}
              icon={Eye}
              label="Open read-only provider workspace"
              note="View Today, Caseload, Schedule, Reports as this provider. Mutations are disabled."
            />
            <ActionLink
              testId="action-shared-calendar"
              to={`/admin/calendar?provider=${provider.id}`}
              icon={CalendarBlank}
              label="Open shared calendar"
              note="See this provider's schedule alongside the team."
            />
            <ActionLink
              testId="action-reports"
              to={`/reports?as_provider=${provider.id}`}
              icon={ChartLineUp}
              label="View provider reports"
              note="Service minutes · fidelity · workload (read-only)."
            />
            <ActionDisabled
              testId="action-message"
              icon={Envelope}
              label="Message provider"
              note="Admin–provider messaging is coming with the messaging refresh. Admins never see provider-to-provider DMs."
            />
          </div>
        </CardContent>
      </Card>

      {/* FP3-105A — Account controls (disable / reactivate). Distinct
          from the availability status picker above. */}
      {canEditStatus && (
        <Card
          data-testid="provider-account-controls"
          className="border-[#E5E3DB] rounded-2xl mt-4"
        >
          <CardContent className="p-5">
            <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font mb-1">
              Account access
            </h3>
            <p className="text-[11.5px] text-[#6B7280] mb-3">
              Disabling immediately blocks sign-in and revokes active
              sessions. This is separate from availability (on leave, paused).
              Student records and history are always preserved.
            </p>
            {["deactivated", "disabled", "removed"].includes(
              (provider.account_status || "").toLowerCase()
            ) ? (
              <Button
                data-testid="provider-reactivate-btn"
                size="sm"
                onClick={() => setAccountAction("reactivate")}
                className="bg-[#1E5A3F] hover:bg-[#174733] text-white text-[12px]"
              >
                <ArrowCounterClockwise size={12} className="mr-1.5" />
                Reactivate account
              </Button>
            ) : (
              <Button
                data-testid="provider-disable-btn"
                size="sm"
                variant="outline"
                onClick={() => setAccountAction("disable")}
                className="border-[#E3C4BE] text-[#8A5A1E] hover:bg-[#FDF8F1] text-[12px]"
              >
                <Prohibit size={12} className="mr-1.5" />
                Disable account
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* FP3-105A — Danger Zone: permanent provider deletion (super only). */}
      {isSuperAdmin && (
        <Card
          data-testid="provider-danger-zone"
          className="border border-[#E3C4BE] bg-[#FDF9F8] rounded-2xl mt-4"
        >
          <CardContent className="p-5">
            <h3 className="text-[13.5px] font-semibold text-[#7A1E1E] heading-font flex items-center gap-1.5">
              <Trash size={14} weight="duotone" /> Danger Zone
            </h3>
            <p className="text-[11.5px] text-[#6B7280] mt-1 mb-3 max-w-xl">
              Permanently deletes this provider's account and active roster
              record. Official student history is preserved as a read-only
              "Former Provider" reference. Cannot be undone.
            </p>
            <Button
              data-testid="provider-permanent-delete-btn"
              size="sm"
              onClick={() => setDeleteOpen(true)}
              className="bg-[#7A1E1E] hover:bg-[#611414] text-white text-[12px]"
            >
              Permanently delete provider…
            </Button>
          </CardContent>
        </Card>
      )}

      <DisableProviderModal
        open={!!accountAction}
        onOpenChange={(o) => {
          if (!o) setAccountAction(null);
        }}
        provider={{ id: provider.id, name: fullName }}
        mode={accountAction || "disable"}
        asOrg={provider.organization_id || ""}
        onDone={load}
      />
      <PlatformProviderDeleteModal
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        provider={{ id: provider.id, name: fullName }}
        onDeleted={() => {
          setDeleteOpen(false);
          toast.success(`${fullName} permanently deleted.`);
          navigate("/admin/providers");
        }}
      />
    </AdminShell>
  );
}

function formatBirthday(iso) {
  if (!iso || typeof iso !== "string" || iso.length < 10) return "";
  // Render as e.g. "Jun 12, 1988" — calm, locale-friendly default.
  try {
    const d = new Date(`${iso}T00:00:00`);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function ProfileField({ icon: Icon, label, value, placeholder, testId }) {
  const shown = value && String(value).trim() ? value : placeholder;
  const muted = !value || !String(value).trim();
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-1.5 text-[12px]"
    >
      <Icon size={12} weight="duotone" className="text-[#3F4756]" />
      <span className="text-[10.5px] uppercase tracking-[0.08em] font-medium text-[#6B7280]">
        {label}
      </span>
      <span className={muted ? "text-[#9AA0A6]" : "text-[#1F2937] font-medium"}>
        {shown}
      </span>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId, tone }) {
  const t = TONES[tone] || TONES.default;
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-2 px-2.5 py-2 rounded-md"
      style={{ backgroundColor: t.bg }}
    >
      <Icon size={13} weight="duotone" style={{ color: t.fg }} />
      <div>
        <div className="text-[9.5px] uppercase tracking-[0.1em] font-semibold opacity-80" style={{ color: t.fg }}>
          {label}
        </div>
        <div className="text-[13px] font-semibold heading-font tracking-tight" style={{ color: t.fg }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function Chip({ label, tone = "default" }) {
  const t = TONES[tone] || TONES.default;
  return (
    <span
      className="inline-flex items-center text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full"
      style={{ backgroundColor: t.bg, color: t.fg }}
    >
      {label}
    </span>
  );
}

function ScopeChip({ scope }) {
  if (!scope) return null;
  const label =
    scope === "platform_wide"
      ? "Platform"
      : scope === "organization_wide"
      ? "Org-wide"
      : "Assigned schools";
  const tone =
    scope === "platform_wide" || scope === "organization_wide" ? "info" : "default";
  return <Chip label={label} tone={tone} />;
}

function StatusChip({ status, tone, testId }) {
  const def = PROVIDER_STATUS_VALUES.find((v) => v.value === status);
  const label = def?.label || status || "—";
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full"
      style={(TONES[tone] || TONES.default)}
    >
      <CircleHalf size={9} weight="duotone" />
      {label}
    </span>
  );
}

function ProviderStatusPicker({ open, current, saving, onToggle, onPick }) {
  return (
    <div className="mt-2.5">
      <button
        data-testid="provider-detail-status-edit"
        onClick={onToggle}
        className="text-[11px] text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
      >
        {open ? "Hide status options" : "Change status…"}
      </button>
      {open && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {PROVIDER_STATUS_VALUES.map((opt) => {
            const isCurrent = opt.value === current;
            const t = TONES[opt.tone] || TONES.default;
            return (
              <button
                key={opt.value}
                data-testid={`provider-detail-status-${opt.value}`}
                onClick={() => !saving && onPick(opt.value)}
                disabled={saving || isCurrent}
                className={`text-[11px] px-2.5 py-0.5 rounded-full transition-colors ${
                  isCurrent ? "ring-1 ring-[#1E3F33]/30" : "hover:opacity-80"
                }`}
                style={{ backgroundColor: t.bg, color: t.fg }}
              >
                {opt.label}
                {isCurrent && <span className="ml-1 opacity-60">✓</span>}
              </button>
            );
          })}
        </div>
      )}
      {open && (
        <p className="text-[10.5px] text-[#9AA0A6] mt-2">
          <strong>On leave / Paused / Unavailable</strong> keep the
          account active so historical data and attribution remain.
          <strong> Inactive</strong> is the only value that signals
          “do not use this account.”
        </p>
      )}
    </div>
  );
}

function ActionLink({ testId, to, icon: Icon, label, note }) {
  return (
    <Link
      data-testid={testId}
      to={to}
      className="flex items-start gap-2.5 px-3 py-2.5 rounded-md border border-[#E5E3DB] hover:border-[#1E3F33]/30 hover:bg-[#FAF9F4] transition-colors"
    >
      <Icon size={14} weight="duotone" className="text-[#1E3F33] mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-medium text-[#1F2937]">{label}</div>
        <div className="text-[10.5px] text-[#6B7280] mt-0.5 leading-snug">{note}</div>
      </div>
    </Link>
  );
}

function ActionDisabled({ testId, icon: Icon, label, note }) {
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-2.5 px-3 py-2.5 rounded-md border border-dashed border-[#E5E3DB] bg-[#FAF9F4]/60 cursor-not-allowed opacity-70"
    >
      <Icon size={14} weight="duotone" className="text-[#6B7280] mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-medium text-[#6B7280]">
          {label}{" "}
          <span className="text-[9.5px] uppercase tracking-[0.08em] ml-1 bg-[#F1F2F4] text-[#6B7280] px-1 py-0.5 rounded">
            Soon
          </span>
        </div>
        <div className="text-[10.5px] text-[#9AA0A6] mt-0.5 leading-snug">{note}</div>
      </div>
    </div>
  );
}

// FP3-029B6A — Per-row routing affordance. Only renders the picker
// (admin-family + primary-with-active-assistants caller). The picker
// lives behind a small "Route" trigger so it doesn't crowd the row
// with selects when the supervising primary has no assistants today.
function StudentRouteRow({ student, provider, canRoute, routeTargets, onAfterRoute }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState(provider.id);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      await api.routeStudentToProvider(student.id, { to_provider_id: picked });
      const target = routeTargets.find((t) => t.id === picked);
      toast.success(
        target
          ? `${student.name} routed to ${target.label}`
          : `${student.name} routed`
      );
      setPickerOpen(false);
      onAfterRoute?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        (detail && typeof detail === "object" && detail.message) ||
        (typeof detail === "string" && detail) ||
        "Could not route student";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li
      data-testid={`provider-detail-student-${student.id}`}
      className="flex items-center justify-between gap-3 py-1.5"
    >
      <Link
        to={`/admin/students/${student.id}`}
        className="flex-1 min-w-0 text-[12.5px] text-[#1F2937] hover:text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px] truncate"
      >
        {student.name}
      </Link>
      <div className="flex items-center gap-1.5 shrink-0 text-[10.5px]">
        {student.grade && (
          <span className="text-[#6B7280] bg-[#F3F1EC] rounded px-1.5 py-0.5">
            Gr {student.grade}
          </span>
        )}
        {student.school_name && (
          <span
            className="inline-flex items-center gap-1 text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2 py-0.5"
            title={student.school_name}
          >
            <Buildings size={9} weight="duotone" />
            <span className="truncate max-w-[120px]">{student.school_name}</span>
          </span>
        )}
        {canRoute && !pickerOpen && (
          <button
            data-testid={`provider-detail-student-${student.id}-route-trigger`}
            onClick={() => {
              setPicked(provider.id);
              setPickerOpen(true);
            }}
            className="text-[10.5px] text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
          >
            Route…
          </button>
        )}
        {canRoute && pickerOpen && (
          <span className="inline-flex items-center gap-1">
            <select
              data-testid={`provider-detail-student-${student.id}-route-select`}
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
              className="h-6 text-[11px] border border-[#E5E3DB] rounded-md px-1.5 bg-white"
            >
              {routeTargets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                  {t.assistant_type ? ` · ${t.assistant_type}` : ""}
                </option>
              ))}
            </select>
            <button
              data-testid={`provider-detail-student-${student.id}-route-save`}
              onClick={submit}
              disabled={saving}
              className="h-6 px-1.5 text-[10px] rounded bg-[#1E3F33] text-white hover:bg-[#173127] disabled:bg-[#1E3F33]/60"
            >
              Route
            </button>
            <button
              onClick={() => setPickerOpen(false)}
              className="h-6 w-6 flex items-center justify-center rounded text-[#6B7280] hover:bg-[#F1F2F4]"
              title="Cancel"
            >
              <X size={9} />
            </button>
          </span>
        )}
      </div>
    </li>
  );
}
